import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  CALL_DIRECTIONS,
  CALL_DIRECTION_LABELS,
  CALL_STATUS_DEFS,
  CALL_STATUS_MAP,
  type CallStatus,
} from "@goyone/shared";
import { api, qs } from "../../lib/api";
import { dateTime } from "../../lib/format";
import { Icon } from "../../components/Icon";
import {
  Badge,
  EmptyState,
  PageHead,
  Pager,
  SearchBox,
} from "../../components/ui";
import { OwnerChip } from "../../components/People";
import { CallRecordingPlayer } from "../../components/CallRecording";
import { Modal } from "../../components/ui";
import { useAuth } from "../../auth/AuthProvider";
import { ReachIcons } from "../../components/Reach";
import {
  AddCallerButton,
  CallDirection,
  identityOf,
  type CallIdentityRow,
} from "../../components/CallIdentity";
import { RecordStats, type RecordStat } from "../../components/RecordStats";
import { CardPickerButton, useCardChoice } from "../../components/CardPicker";
import {
  ColumnPickerButton,
  useColumnChoice,
  useColumnDrag,
  type ColumnDef,
} from "../../components/ColumnPicker";
import {
  FilterButton,
  FilterChips,
  FilterScope,
  HeaderFilter,
  useColumnFilters,
  type FilterDef,
} from "../../components/ColumnFilter";

/**
 * سجلّ المكالمات.
 *
 * كل صفٍّ مكالمة حقيقية بكاملها: من ومتى وكم دامت وبمَ انتهت — والملف
 * الذي دخلت فيه، وتسجيلها الصوتي إن التُقط عبر جلسة تسجيل الوردية
 * في الكونسول — يُشغَّل من الصفّ مباشرة.
 */

/** كتالوج أعمدة السجلّ. */
const CALL_COLUMNS: readonly ColumnDef[] = [
  /* الاتجاهُ عمودٌ لا وسمٌ في العميل: به يُفرز الواردُ من الصادر بالعين. */
  { key: "direction", label: "الاتجاه" },
  { key: "client", label: "العميل", fixed: true },
  { key: "phone", label: "الرقم" },
  { key: "reach", label: "تواصل" },
  { key: "agent", label: "الموظف" },
  { key: "line", label: "خطّ الشركة" },
  { key: "status", label: "الحالة" },
  { key: "disposition", label: "النتيجة" },
  { key: "duration", label: "المدة", group: "الأرقام" },
  { key: "recording", label: "التسجيل" },
  { key: "quality", label: "الجودة", group: "الأرقام" },
  { key: "when", label: "متى" },
];

/** أعمدة زرّ «فلتر» — تقابل `CALL_FILTERABLE` في callcenter.service.ts. */
const CALL_FILTERS: FilterDef[] = [
  {
    key: "direction",
    label: "الاتجاه",
    kind: "enum",
    options: CALL_DIRECTIONS.map((d) => ({
      value: d,
      label: CALL_DIRECTION_LABELS[d],
    })),
  },
  { key: "phone", label: "الرقم", kind: "text" },
  { key: "agent", label: "الموظف", kind: "user" },
  { key: "line", label: "خطّ الشركة", kind: "text" },
  {
    key: "status",
    label: "الحالة",
    kind: "enum",
    options: CALL_STATUS_DEFS.map((s) => ({ value: s.key, label: s.label })),
  },
  { key: "disposition", label: "النتيجة", kind: "text", noList: true },
  { key: "duration", label: "المدة", kind: "number" },
  { key: "quality", label: "الجودة", kind: "number" },
  { key: "when", label: "متى", kind: "date" },
];

/** ما يُعرض من البطاقات قبل أن يختار المستخدم. */
const DEFAULT_CALL_CARDS = ["completed", "noAnswer", "outbound", "avgDuration"];

/** شكل ردّ ‎/callcenter/stats‎ — ما تحتاجه البطاقات منه. */
interface CallStats {
  byStatus: Record<string, number>;
  byDirection: Record<string, number>;
  avgDurationSec: number;
  totalDurationSec: number;
}

export function CallsHistory() {
  const [listening, setListening] = useState<Record<string, any> | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState<CallStatus | "">("");
  const [direction, setDirection] = useState<"" | "OUTBOUND" | "INBOUND">("");
  const [q, setQ] = useState("");
  /* الصفحة تحفظ استعلامها في الحالة لا في الرابط، فشروط «فلتر» معها هنا. */
  const [f, setF] = useState<string | undefined>(undefined);
  const { filters, setFilters } = useColumnFilters(
    f ? { f } : {},
    (changes) => {
      setF(changes.f);
      setPage(1);
    },
  );

  const query = {
    page,
    pageSize,
    q: q || undefined,
    status: status || undefined,
    direction: direction || undefined,
    f,
  };

  const calls = useQuery({
    queryKey: ["cc", "history", query],
    queryFn: () =>
      api<{
        items: Array<Record<string, any>>;
        total: number;
        page: number;
        pageCount: number;
      }>(`/callcenter/calls${qs(query)}`),
  });

  /* الإحصاء للإدارة وحدها — المسار نفسه محميّ بدور المدير في الخادم. */
  const { atLeast } = useAuth();
  const manager = atLeast("MANAGER");

  const stats = useQuery({
    enabled: manager,
    queryKey: ["cc", "stats", "30d"],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
      return api<CallStats>(
        `/callcenter/stats${qs({ from: from.toISOString(), to: to.toISOString() })}`,
      );
    },
  });

  const cardChoice = useCardChoice("goyone.stats.calls", DEFAULT_CALL_CARDS);
  const statItems = stats.data ? callCards(stats.data) : [];
  const statChoices = statItems.map(({ key, label, group }) => ({
    key,
    label,
    group,
  }));

  const {
    visible: shownColumns,
    state: columnState,
    setState: setColumnState,
    reset: resetColumns,
    touched: columnsTouched,
  } = useColumnChoice("goyone.columns.calls", CALL_COLUMNS);
  const colDrag = useColumnDrag(columnState, setColumnState);

  return (
    <div className="page">
      <PageHead
        title="سجلّ المكالمات"
        subtitle={calls.data ? `${calls.data.total} مكالمة` : undefined}
      />

      {/* بطاقات الإدارة — أرقام آخر ثلاثين يومًا من مسار الإحصاء المحميّ. */}
      {manager && stats.data && (
        <RecordStats
          items={statItems}
          storageKey="goyone.stats.calls"
          defaults={DEFAULT_CALL_CARDS}
          external={cardChoice}
        />
      )}

      {/*
        الشريط بترتيب القوائم الموحَّد: بحثٌ فمرشّحات، وفي طرفه الأعمدة
        والبطاقات وزرّ الكونسول. يلتصق أعلى الصفحة عند التمرير.
      */}
      <div className="toolbar sticky-bar">
        <SearchBox
          value={q}
          onChange={(v) => {
            setQ(v);
            setPage(1);
          }}
          placeholder="بحث في الرقم أو الاسم أو الملاحظات"
        />
        <select
          className="select"
          style={{ width: "auto" }}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as CallStatus | "");
            setPage(1);
          }}
          aria-label="الحالة"
        >
          <option value="">كل الحالات</option>
          {CALL_STATUS_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
        <select
          className="select"
          style={{ width: "auto" }}
          value={direction}
          onChange={(e) => {
            setDirection(e.target.value as "" | "OUTBOUND" | "INBOUND");
            setPage(1);
          }}
          aria-label="الاتجاه"
        >
          <option value="">الكل</option>
          <option value="OUTBOUND">صادرة</option>
          <option value="INBOUND">واردة</option>
        </select>

        <span
          className="row"
          style={{ marginInlineStart: "auto", gap: "var(--sp-2)" }}
        >
          <FilterButton
            defs={CALL_FILTERS}
            filters={filters}
            onChange={setFilters}
          />
          <ColumnPickerButton
            columns={CALL_COLUMNS}
            state={columnState}
            onChange={setColumnState}
            onReset={resetColumns}
            touched={columnsTouched}
          />
          {manager && (
            <CardPickerButton
              choices={statChoices}
              chosen={cardChoice.chosen}
              onChange={cardChoice.setChosen}
              onReset={cardChoice.reset}
            />
          )}
          <Link to="/callcenter" className="btn btn-primary btn-sm">
            <Icon name="phone" size={14} />
            الكونسول
          </Link>
        </span>
      </div>

      <FilterChips
        defs={CALL_FILTERS}
        filters={filters}
        onChange={setFilters}
      />

      <FilterScope
        defs={CALL_FILTERS}
        filters={filters}
        onChange={setFilters}
        distinctPath="/callcenter/calls/distinct"
        query={query}
      >
      <div className="card">
        {calls.isPending && (
          <div className="skeleton" style={{ height: 240 }} />
        )}

        {calls.data?.items.length === 0 && (
          <EmptyState
            title="لا مكالمات"
            text="ما يُسجَّل من الكونسول يظهر هنا."
          />
        )}

        {/* رأسُ الجدول يثبت كجدول العملاء: الصفحةُ تصعد حتى يلتصق، ثم يجري السجلُّ داخله. */}
        {(calls.data?.items.length ?? 0) > 0 && (
          <div className="table-wrap sticky-head">
            <table className="table">
              <thead>
                <tr>
                  <th />
                  {shownColumns.map((c) => (
                    <th key={c.key} {...colDrag.thProps(c)}>
                      {c.label}
                      <HeaderFilter colKey={c.key} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {calls.data?.items.map((c) => {
                  return (
                    <tr key={c.id}>
                      <td>
                        <Icon
                          name={c.direction === "OUTBOUND" ? "send" : "inbox"}
                          size={14}
                        />
                      </td>
                      {/* إعادة الاتصال والرسائل صارت في عمود «تواصل» الموحَّد. */}
                      {shownColumns.map((col) => (
                        <CallCell
                          key={col.key}
                          column={col}
                          call={c}
                          onListen={() => setListening(c)}
                        />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {calls.data && (
          <Pager
            page={calls.data.page}
            pageCount={calls.data.pageCount}
            total={calls.data.total}
            pageSize={pageSize}
            onPage={setPage}
            onPageSize={(n) => {
              setPageSize(n);
              setPage(1);
            }}
          />
        )}
      </div>
      </FilterScope>

      {listening && (
        <RecordingModal call={listening} onClose={() => setListening(null)} />
      )}
    </div>
  );
}

/* ==========================================================================
   نافذة الاستماع — المشغّل باسم العميل والتقييم
   ========================================================================== */

function RecordingModal({
  call,
  onClose,
}: {
  call: Record<string, any>;
  onClose: () => void;
}) {
  const name =
    call.lead?.name ?? call.contact?.name ?? call.account?.name ?? call.phone;
  return (
    <Modal title="تسجيل المكالمة" onClose={onClose}>
      <CallRecordingPlayer
        id={call.recordingUrl}
        title={name}
        meta={
          <span
            className="row"
            style={{ gap: "var(--sp-2)", flexWrap: "wrap" }}
          >
            {call.disposition && (
              <Badge kind={call.disposition.kind}>
                {call.disposition.label}
              </Badge>
            )}
            {call.review && (
              <Badge kind="info">الجودة: {call.review.total}/100</Badge>
            )}
          </span>
        }
      />

      {/* الفيدباك المأخوذ على هذه المكالمة — تحت المشغّل حيث يُقرأ مع الصوت. */}
      {call.review?.feedback ? (
        <div className="rec-feedback">
          <span className="label">ملاحظات الجودة</span>
          <p className="small">{call.review.feedback}</p>
        </div>
      ) : call.note ? (
        <div className="rec-feedback">
          <span className="label">ملاحظة المكالمة</span>
          <p className="small">{call.note}</p>
        </div>
      ) : null}
    </Modal>
  );
}

/** «٣:٢٤» للدقائق، و«٤٥ث» لما دون الدقيقة. */
function formatDuration(sec: number): string {
  if (sec < 60) return `${sec}ث`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

/** خلية عمودٍ واحد — ترتيبها وإظهارها بيد المستخدم من منتقي الأعمدة. */
function CallCell({
  column,
  call: c,
  onListen,
}: {
  column: ColumnDef;
  call: Record<string, any>;
  onListen: () => void;
}) {
  switch (column.key) {
    case "direction":
      return (
        <td>
          <CallDirection direction={c.direction} />
        </td>
      );
    case "client": {
      /*
       * الاسمُ وتبعيّتُه: جهةُ الاتصال باسم حسابها، والمحتملُ بوسمه.
       * والمجهولُ لا يُترك «غير معروف» وحسب — يحمل زرَّ إضافته.
       */
      const who = identityOf(c as CallIdentityRow);
      return (
        <td>
          {who.name ? (
            <span className="cc-who">
              {who.link ? (
                <Link
                  to={who.link}
                  className="link"
                  onClick={(e) => e.stopPropagation()}
                >
                  {who.name}
                </Link>
              ) : (
                <span>{who.name}</span>
              )}
              {who.belongsTo && (
                <span className="tiny mute">{who.belongsTo}</span>
              )}
            </span>
          ) : (
            <span className="row" style={{ gap: 6 }}>
              <span className="tiny mute">غير مسجَّل</span>
              <AddCallerButton call={c as CallIdentityRow} />
            </span>
          )}
        </td>
      );
    }
    case "phone":
      return (
        <td className="num" dir="ltr">
          {c.phone}
        </td>
      );
    case "reach":
      /* الأيقونات الموحّدة: اتصال من الكونسول، واتساب داخل القنوات، بريد الشركة، وSMS من الجهاز. */
      return (
        <td>
          <ReachIcons
            name={c.lead?.name ?? c.contact?.name ?? c.account?.name ?? c.phone}
            phone={c.phone}
            email={
              c.lead?.email ?? c.contact?.email ?? c.account?.email ?? null
            }
            link={{
              leadId: c.leadId,
              accountRefId: c.accountId,
              contactId: c.contactId,
            }}
            size={14}
          />
        </td>
      );
    case "agent":
      return (
        <td>
          <OwnerChip ownerId={c.agentId} size={22} />
        </td>
      );
    case "line":
      return (
        <td className="num tiny" dir="ltr">
          {c.agentPhone ?? "—"}
        </td>
      );
    case "status":
      return (
        <td>
          <Badge
            kind={CALL_STATUS_MAP[c.status as CallStatus]?.kind ?? "neutral"}
          >
            {CALL_STATUS_MAP[c.status as CallStatus]?.label ?? c.status}
          </Badge>
        </td>
      );
    case "disposition":
      return (
        <td>
          {c.disposition ? (
            <Badge kind={c.disposition.kind}>{c.disposition.label}</Badge>
          ) : (
            "—"
          )}
        </td>
      );
    case "duration":
      return (
        <td className="num">
          {c.durationSec != null ? formatDuration(c.durationSec) : "—"}
        </td>
      );
    case "recording":
      /* المشغّل الكامل يُفتح في نافذة باسم العميل وتقييمه. */
      return (
        <td>
          {c.recordingUrl ? (
            String(c.recordingUrl).startsWith("http") ? (
              <a
                className="link tiny"
                href={c.recordingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                فتح
              </a>
            ) : (
              <button
                className="btn btn-sm btn-icon btn-ghost"
                onClick={onListen}
                title="استماع"
                aria-label="استماع للتسجيل"
              >
                <Icon name="video" size={15} />
              </button>
            )
          ) : (
            "—"
          )}
        </td>
      );
    case "quality":
      return (
        <td className="num">{c.review ? `${c.review.total}/100` : "—"}</td>
      );
    case "when":
      return <td className="tiny mute">{dateTime(c.startedAt)}</td>;
    default:
      return <td>—</td>;
  }
}

/**
 * بطاقات السجلّ من مسار الإحصاء — أرقام المجموعة كلها لا الصفحة، على
 * مدى ثلاثين يومًا يطابق ما تعرضه لوحة الأداء.
 */
function callCards(s: CallStats): RecordStat[] {
  const st = s.byStatus ?? {};
  const dir = s.byDirection ?? {};
  const period = "آخر ٣٠ يومًا";
  return [
    {
      key: "completed",
      label: "مكالمات تمّت",
      group: "الحالة",
      icon: "check",
      color: "#51A521",
      value: st.COMPLETED ?? 0,
      numeric: true,
      note: period,
    },
    {
      key: "noAnswer",
      label: "لم يُجَب",
      group: "الحالة",
      icon: "alert",
      color: "#BF930B",
      value: st.NO_ANSWER ?? 0,
      numeric: true,
      note: period,
    },
    {
      key: "missed",
      label: "فائتة",
      group: "الحالة",
      icon: "alert",
      color: "#DC2626",
      value: st.MISSED ?? 0,
      numeric: true,
      note: period,
    },
    {
      key: "outbound",
      label: "صادرة",
      group: "الاتجاه",
      icon: "send",
      color: "#3672EA",
      value: dir.OUTBOUND ?? 0,
      numeric: true,
      note: period,
    },
    {
      key: "inbound",
      label: "واردة",
      group: "الاتجاه",
      icon: "inbox",
      color: "#5596FF",
      value: dir.INBOUND ?? 0,
      numeric: true,
      note: period,
    },
    {
      key: "avgDuration",
      label: "متوسط المدة",
      group: "المدة",
      icon: "clock",
      color: "#1D46A6",
      value: formatDuration(s.avgDurationSec ?? 0),
      numeric: true,
      note: `للمكالمات التامّة — ${period}`,
    },
    {
      key: "totalDuration",
      label: "إجمالي وقت المكالمات",
      group: "المدة",
      icon: "clock",
      color: "#8791A1",
      value: formatDuration(s.totalDurationSec ?? 0),
      numeric: true,
      note: period,
    },
  ];
}
